import { stringifyGraph } from '@/clients/http'
import { CategoryModel } from '@/http/models/ProductCategory'
import { Check, ChevronLeft, ChevronRight, Home, Search } from 'lucide-react'

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'

import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { fieldClass } from '@/components/ui/field-class'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

import { ProductCategoryModel } from '@/types'
import { Spinner } from '@/components/ui/spinner'

interface CategoryTreeSelectProps {
  value?: string | null
  onChange: (value: string | null, label: string) => void
  placeholder?: string
  disabled?: boolean
  excludeId?: string
  defaultLabel?: string
  selectionMode?: 'product' | 'category' // 'product' for leaf-only, 'category' for parent selection
}

export const CategoryTreeSelect = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  excludeId,
  defaultLabel,
  selectionMode = 'category',
}: CategoryTreeSelectProps) => {
  const [open, setOpen] = useState(false)
  const defaultPlaceholder =
    selectionMode === 'product' ? 'Select product category...' : 'Select parent category...'
  const currentPlaceholder = placeholder || defaultPlaceholder
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState<ProductCategoryModel.ProductCategory[]>([])
  const [currentParentId, setCurrentParentId] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLabel, setSelectedLabel] = useState(defaultLabel || '')

  const fetchCategories = useCallback(async () => {
    setLoading(true)
    try {
      const response = await CategoryModel.getList({
        query: {
          length: 500,
          status: 'active',
          graph_enc: stringifyGraph({
            '*': true,
            'children.count': true,
            'products.count': true,
          }),
        },
      })
      if (response.success) {
        setCategories(response.data.result)
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open && categories.length === 0) {
      fetchCategories()
    }
  }, [open, categories.length, fetchCategories])

  useEffect(() => {
    if (defaultLabel) setSelectedLabel(defaultLabel)
  }, [defaultLabel])

  const filteredCategories = useMemo(() => {
    let list = categories
    if (excludeId) {
      list = list.filter((c) => c._id !== excludeId)
    }

    if (searchTerm) {
      return list.filter((c) => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
    }

    return list.filter((c) => c.parent_id === currentParentId)
  }, [categories, currentParentId, searchTerm, excludeId])

  const currentParent = useMemo(() => {
    if (!currentParentId) return null
    return categories.find((c) => c._id === currentParentId)
  }, [categories, currentParentId])

  const checkHasChildren = (category: ProductCategoryModel.ProductCategory) => {
    if (typeof category.children_count === 'number') {
      return category.children_count > 0
    }
    return categories.some((c) => c.parent_id === category._id)
  }

  const isSelectable = (category: ProductCategoryModel.ProductCategory) => {
    if (selectionMode === 'product') {
      // Products can only be assigned to leaf categories (no children)
      return !checkHasChildren(category)
    }
    if (selectionMode === 'category') {
      // Categories can only have parents that don't have direct products
      return (category.products_count || 0) === 0
    }
    return true
  }

  const handleSelect = (category: ProductCategoryModel.ProductCategory | null) => {
    if (!category) {
      onChange(null, '')
      setSelectedLabel('')
    } else {
      // If not selectable, we might want to navigate instead if it has children
      if (!isSelectable(category)) {
        if (checkHasChildren(category) && !searchTerm) {
          setCurrentParentId(category._id)
        }
        return
      }
      onChange(category._id, category.name)
      setSelectedLabel(category.name)
    }
    setOpen(false)
  }

  const breadcrumbs = useMemo(() => {
    const parts: { id: string | null; name: string }[] = [{ id: null, name: 'Root' }]
    if (currentParentId) {
      let current = categories.find((c) => c._id === currentParentId)
      const path = []
      while (current) {
        path.unshift({ id: current._id, name: current.name })
        current = categories.find((c) => c._id === current.parent_id)
      }
      parts.push(...path)
    }
    return parts
  }, [categories, currentParentId])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            fieldClass,
            'h-8 justify-between font-normal',
            !selectedLabel && 'text-muted-foreground'
          )}
          disabled={disabled}
        >
          <span className="truncate">{selectedLabel || currentPlaceholder}</span>
          <Search className="text-muted-foreground" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 sm:w-96" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search categories"
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList className="max-h-[350px]">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Spinner className="size-4" />
                Loading…
              </div>
            ) : (
              <>
                {!searchTerm && (
                  <div className="flex flex-wrap items-center gap-1 border-b border-border-subtle bg-subdued px-2 py-1.5">
                    {breadcrumbs.map((crumb, index) => (
                      <Fragment key={crumb.id || 'root'}>
                        {index > 0 && <span className="text-muted-foreground">/</span>}
                        <button
                          className={cn(
                            'rounded-sm px-1.5 py-0.5 text-xs transition-colors hover:bg-fill',
                            index === breadcrumbs.length - 1
                              ? 'font-semibold text-ink'
                              : 'text-muted-foreground'
                          )}
                          onClick={() => {
                            setCurrentParentId(crumb.id)
                            setSearchTerm('')
                          }}
                        >
                          {crumb.name}
                        </button>
                      </Fragment>
                    ))}
                  </div>
                )}

                <CommandEmpty>No categories found.</CommandEmpty>
                <CommandGroup>
                  {currentParentId && !searchTerm && (
                    <CommandItem
                      onSelect={() => {
                        const parent = categories.find((c) => c._id === currentParentId)
                        setCurrentParentId(parent?.parent_id || null)
                      }}
                      className="font-medium text-ink"
                    >
                      <ChevronLeft className="mr-2 size-4" />
                      Back to{' '}
                      {categories.find((c) => c._id === currentParentId)?.parent_id
                        ? 'parent'
                        : 'roots'}
                    </CommandItem>
                  )}

                  {/* Option to select "None" if at root */}
                  {!currentParentId && !searchTerm && (
                    <CommandItem onSelect={() => handleSelect(null)}>
                      <Check
                        className={cn('mr-2 size-4', value === null ? 'opacity-100' : 'opacity-0')}
                      />
                      None (top level)
                    </CommandItem>
                  )}

                  {filteredCategories.map((category) => {
                    const selectable = isSelectable(category)
                    const hasChilds = checkHasChildren(category)

                    return (
                      <CommandItem
                        key={category._id}
                        onSelect={() => handleSelect(category)}
                        className={cn(
                          'group flex cursor-pointer items-center justify-between',
                          !selectable && 'opacity-70'
                        )}
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <Check
                            className={cn(
                              'mr-2 size-4 shrink-0',
                              value === category._id ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <div className="flex flex-col min-w-0">
                            <span
                              className={cn(
                                'truncate',
                                value === category._id && 'font-medium text-ink'
                              )}
                            >
                              {category.name}
                            </span>
                            {searchTerm && category.parent_id && (
                              <span className="truncate text-xs text-muted-foreground">
                                in{' '}
                                {categories.find((c) => c._id === category.parent_id)?.name ||
                                  '...'}
                              </span>
                            )}
                            {!selectable && (
                              <span className="text-xs text-error">
                                {selectionMode === 'product'
                                  ? 'Has sub-categories'
                                  : 'Has products'}
                              </span>
                            )}
                          </div>
                        </div>

                        {!searchTerm && hasChilds && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Show sub-categories of ${category.name}`}
                            className="z-10 ml-2"
                            onClick={(e) => {
                              e.stopPropagation()
                              setCurrentParentId(category._id)
                            }}
                          >
                            <ChevronRight />
                          </Button>
                        )}
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
